import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideLoader, lucidePaperclip, lucideSend, lucideX } from '@ng-icons/lucide';
import { BrnDialogImports } from '@spartan-ng/brain/dialog';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { SessionSnapshot, sessionSnapshot } from '@core/services/session-context';
import { HelpService } from '@features/help/services/help.service';
import { SupportKind } from '@features/help/models/help.model';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { ToastService } from '@shared/services/toast.service';
import { KENERGY_FIELD_CHROME_WITHIN, KENERGY_FIELD_TEXT } from '@shared/spartan/kenergy-field';

/** Must match the server's limits in support-request.controller.ts; other image formats are converted to PNG. */
const MAX_SCREENSHOTS = 5;
const MAX_SCREENSHOT_BYTES = 8 * 1024 * 1024;
const SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_MESSAGE_LENGTH = 5000;

const COPY: Record<
  SupportKind,
  { title: string; description: string; placeholder: string; success: [string, string] }
> = {
  feedback: {
    title: 'Feedback senden',
    description: 'Fehler, Verbesserungsvorschläge oder Feature-Wünsche – alles landet direkt bei unserem Produktteam.',
    placeholder: 'Was ist passiert oder was wünschen Sie sich?',
    success: ['Danke für Ihr Feedback!', 'Es ist direkt bei unserem Produktteam angekommen.'],
  },
  help: {
    title: 'Hilfe anfragen',
    description: 'Fragen zur Nutzung oder ein Problem? Wir antworten Ihnen per E-Mail.',
    placeholder: 'Wobei können wir helfen?',
    success: ['Anfrage gesendet', 'Wir melden uns so schnell wie möglich per E-Mail.'],
  },
};

/**
 * One quick dialog for feedback and help requests, opened from the header so
 * the user never leaves the page they are on. Screenshots come in by pasting,
 * dropping or the paperclip; the session context (see session-context.ts) is
 * taken when the dialog opens and sent along. The draft survives closing the
 * dialog and is cleared only once it has been sent.
 */
@Component({
  selector: 'app-support-dialog',
  imports: [BrnDialogImports, HlmDialogImports, HlmButtonDirective, NgIconComponent],
  providers: [provideIcons({ lucideLoader, lucidePaperclip, lucideSend, lucideX })],
  templateUrl: './support-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportDialog {
  private readonly helpService = inject(HelpService);
  private readonly currentOrganization = inject(CurrentOrganizationStore);
  private readonly toast = inject(ToastService);

  protected readonly fieldChrome = KENERGY_FIELD_CHROME_WITHIN;
  protected readonly fieldText = KENERGY_FIELD_TEXT;
  protected readonly maxMessageLength = MAX_MESSAGE_LENGTH;
  protected readonly maxScreenshots = MAX_SCREENSHOTS;
  protected readonly screenshotTypes = SCREENSHOT_TYPES.join(',');

  protected readonly state = signal<'open' | 'closed'>('closed');
  protected readonly kind = signal<SupportKind>('feedback');
  protected readonly message = signal('');
  protected readonly screenshots = signal<{ file: File; url: string }[]>([]);
  protected readonly context = signal<SessionSnapshot | null>(null);
  protected readonly sending = signal(false);

  protected readonly copy = computed(() => COPY[this.kind()]);
  protected readonly canSend = computed(() => /\S/.test(this.message()) && !this.sending());

  open(kind: SupportKind): void {
    this.kind.set(kind);
    this.context.set(sessionSnapshot());
    this.state.set('open');
  }

  protected onPaste(event: ClipboardEvent): void {
    const data = event.clipboardData;
    // Text wins: copying from a document often carries an image rendition too.
    if (!data || data.types.includes('text/plain') || data.files.length === 0) return;
    event.preventDefault();
    void this.addScreenshots([...data.files]);
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    void this.addScreenshots([...(event.dataTransfer?.files ?? [])]);
  }

  protected onFilesSelected(input: HTMLInputElement): void {
    void this.addScreenshots([...(input.files ?? [])]);
    input.value = '';
  }

  protected removeScreenshot(index: number): void {
    URL.revokeObjectURL(this.screenshots()[index].url);
    this.screenshots.update((list) => list.filter((_, i) => i !== index));
  }

  protected async send(): Promise<void> {
    const organizationId = this.currentOrganization.organizationId();
    const context = this.context();
    if (!this.canSend() || !organizationId || !context) return;

    this.sending.set(true);
    try {
      await this.helpService.submitSupportRequest(organizationId, {
        kind: this.kind(),
        message: this.message().trim(),
        context,
        screenshots: this.screenshots().map((shot) => shot.file),
      });
      this.toast.success(...this.copy().success);
      this.screenshots().forEach((shot) => URL.revokeObjectURL(shot.url));
      this.screenshots.set([]);
      this.message.set('');
      this.state.set('closed');
    } catch (error) {
      console.error('Support request failed:', error);
      this.toast.error('Senden fehlgeschlagen', 'Ihre Nachricht ist noch da. Bitte versuchen Sie es erneut.');
    } finally {
      this.sending.set(false);
    }
  }

  private async addScreenshots(files: File[]): Promise<void> {
    const converted = await Promise.all(files.map(toSupportedImage));
    const unreadable = files.filter((_, i) => !converted[i]);
    if (unreadable.length) {
      const formats = unreadable.map((file) => file.type || file.name).join(', ');
      console.warn('Unreadable attachment:', unreadable);
      this.toast.warning('Format nicht unterstützt', `${formats} – bitte als PNG oder JPEG anhängen.`);
    }
    const images = converted.filter((file): file is File => !!file);
    const fitting = images.filter((file) => file.size <= MAX_SCREENSHOT_BYTES);
    if (fitting.length < images.length) {
      this.toast.warning('Bild zu groß', 'Ein Screenshot darf höchstens 8 MB groß sein.');
    }
    const room = MAX_SCREENSHOTS - this.screenshots().length;
    if (fitting.length > room) {
      this.toast.warning('Zu viele Bilder', `Maximal ${MAX_SCREENSHOTS} Screenshots pro Nachricht.`);
    }
    const added = fitting.slice(0, Math.max(room, 0)).map((file) => ({ file, url: URL.createObjectURL(file) }));
    this.screenshots.update((list) => [...list, ...added]);
  }
}

/**
 * Keeps PNG/JPEG/WebP/GIF as they are and re-encodes anything else the browser
 * can decode (TIFF/HEIC on Safari, BMP, AVIF, files with no declared type) as
 * PNG, since clipboards and screenshot tools don't always label images the way
 * the server expects. Returns null if the browser can't read it as an image.
 */
async function toSupportedImage(file: File): Promise<File | null> {
  if (SCREENSHOT_TYPES.includes(file.type)) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    return blob && new File([blob], 'screenshot.png', { type: 'image/png' });
  } catch {
    return null;
  }
}
